import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Building, Plus, Edit, Trash2, Save, X, Package } from 'lucide-react';
import { getStructures, createStructure, updateStructure, deleteStructure } from '../../api';

const StructureManagement = () => {
  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingStructure, setEditingStructure] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'creche',
    description: '',
  });
  const [structureStats, setStructureStats] = useState({});

  useEffect(() => {
    fetchStructures();
  }, []);

  const fetchStructures = async () => {
    try {
      setLoading(true);
      const response = await getStructures();
      setStructures(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des structures');
      console.error('Error fetching structures:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingStructure) {
        await updateStructure(editingStructure.id, formData);
      } else {
        await createStructure(formData);
      }
      await fetchStructures();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (structureId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette structure ? Cette action est irréversible.')) {
      return;
    }
    try {
      await deleteStructure(structureId);
      await fetchStructures();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleEdit = (structure) => {
    setEditingStructure(structure);
    setFormData({
      name: structure.name,
      type: structure.type || 'creche',
      description: structure.description || '',
    });
    setShowAddForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'creche',
      description: '',
    });
    setEditingStructure(null);
    setShowAddForm(false);
  };

  if (loading) {
    return <div className="p-4 text-center">Chargement...</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center mb-6">
        <h1 className="text-3xl font-bold">Gestion des structures</h1>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-800 rounded-lg">
          {error}
        </div>
      )}

      {/* Formulaire d'ajout/édition */}
      {showAddForm && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex justify-between items-center">
              <span>{editingStructure ? 'Modifier' : 'Ajouter'} une structure</span>
              <button onClick={resetForm} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nom de la structure *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full p-2 border rounded-lg"
                    placeholder="Ex: Crèche Choupi"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Type *</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full p-2 border rounded-lg"
                  >
                    <option value="creche">Crèche</option>
                    <option value="siege">Siège</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full p-2 border rounded-lg"
                  rows="3"
                  placeholder="Description optionnelle de la structure"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  type="submit"
                  className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition"
                >
                  <Save size={20} />
                  {editingStructure ? 'Mettre à jour' : 'Créer'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-100 transition"
                >
                  Annuler
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Liste des structures */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {structures.map(structure => (
          <Card key={structure.id} className="hover:shadow-lg transition">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="text-primary" size={24} />
                <span>{structure.name}</span>
                <span className={`ml-auto text-xs font-medium px-2 py-0.5 rounded ${
                  structure.type === 'siege' ? 'bg-indigo-100 text-indigo-700' :
                  structure.type === 'autre' ? 'bg-gray-100 text-gray-600' :
                  'bg-primary/10 text-primary-dark'
                }`}>
                  {structure.type === 'siege' ? 'Siège' : structure.type === 'autre' ? 'Autre' : 'Crèche'}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {structure.description && (
                  <p className="text-gray-600 text-sm">{structure.description}</p>
                )}

                <div className="pt-3 border-t">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Package size={16} />
                    <span>ID: {structure.id}</span>
                  </div>
                  {structure.createdAt && (
                    <div className="text-xs text-gray-400 mt-1">
                      Créée le: {new Date(structure.createdAt).toLocaleDateString('fr-FR')}
                    </div>
                  )}
                </div>

                <div className="flex gap-2 pt-3">
                  <button
                    onClick={() => handleEdit(structure)}
                    className="flex-1 flex items-center justify-center gap-2 p-2 text-primary border border-primary rounded-lg hover:bg-primary/5 transition"
                  >
                    <Edit size={18} />
                    Modifier
                  </button>
                  <button
                    onClick={() => handleDelete(structure.id)}
                    className="flex-1 flex items-center justify-center gap-2 p-2 text-red-600 border border-red-600 rounded-lg hover:bg-red-50 transition"
                  >
                    <Trash2 size={18} />
                    Supprimer
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {structures.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Building size={48} className="mx-auto mb-4 text-gray-300" />
          <p>Aucune structure créée</p>
          <p className="text-sm">Utilisez le bouton + pour créer une structure</p>
        </div>
      )}
      {!showAddForm && (
        <button
          onClick={() => setShowAddForm(true)}
          className="fixed bottom-6 right-6 z-50 bg-primary text-white rounded-full p-4 shadow-lg hover:bg-primary-dark transition-all hover:scale-105"
          title="Nouvelle structure"
        >
          <Plus size={24} />
        </button>
      )}
    </div>
  );
};

export default StructureManagement;
